import { Show, onMount, onCleanup, type JSX, type ParentProps } from "solid-js";
import "./drawer.css";

export interface DrawerProps extends ParentProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  side?: "right" | "left";
  width?: string;
}

/**
 * Composant Drawer réutilisable — panneau latéral coulissant.
 *
 * Props :
 * - `open: boolean` — contrôle la visibilité du panneau
 * - `onClose: () => void` — callback appelé lors de la fermeture (clic overlay ou Escape)
 * - `title?: string` — titre du panneau (optionnel)
 * - `side?: "right" | "left"` — côté d'origine de la glisse (défaut "right")
 * - `width?: string` — largeur du panneau (défaut "420px")
 * - `children` — contenu du panneau
 *
 * Comportement :
 * - Overlay semi-transparent avec blur ; clic → onClose()
 * - Transition CSS fluide
 * - En-tête avec titre + bouton de fermeture (✕)
 * - Touche Escape ferme le drawer
 * - Focus au ouverture (a11y), role="dialog" + aria-modal="true"
 */
export function Drawer(props: DrawerProps): JSX.Element {
  const side = () => props.side || "right";
  const width = () => props.width || "420px";

  // Gestion de la touche Escape
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape" && props.open) {
      e.preventDefault();
      props.onClose();
    }
  };

  onMount(() => {
    document.addEventListener("keydown", handleKeyDown);
  });

  onCleanup(() => {
    document.removeEventListener("keydown", handleKeyDown);
  });

  return (
    <Show when={props.open}>
      <div
        class="drawer-overlay"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) {
            props.onClose?.();
          }
        }}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        ref={(el) => {
          setTimeout(() => el?.focus(), 10);
        }}
      >
        <div class={`drawer drawer--${side()}`} style={{ width: width() }} role="complementary">
          <div class="drawer__header">
            {props.title && <h2 class="drawer__title">{props.title}</h2>}
            <button class="drawer__close" aria-label="Fermer" onClick={() => props.onClose?.()}>
              ✕
            </button>
          </div>
          <div class="drawer__body">{props.children}</div>
        </div>
      </div>
    </Show>
  );
}
